import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key';
import { isWorkbookWritable } from 'cadenza/workbook/workbook.state';
import { getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookLayerType } from 'cadenza/workbook/model/workbook-layer-type';
import { assertSingleWorkbookLayerType } from 'cadenza/workbook/model/workbook-layer-type';
import type { CatalogLayerType } from 'cadenza/catalog-layers/catalog-layer-type';
import { assertUnreachable } from 'cadenza/utils/custom-error';
import { ExternalLayerServiceType } from 'cadenza/workbook/model/external-layer-service-type';

export function getWorkbookLayerType(layer: WorkbookLayerDto): WorkbookLayerType {
  switch (layer.structureType) {
    case 'TOGGLE':
      return 'TOGGLE_GROUP';
    case 'GROUP':
      return 'STANDARD_GROUP';
    case 'SINGLE': {
      if (isDataViewLayer(layer)) {
        return 'DATA_VIEW';
      }
      if (isSketchLayer(layer)) {
        return 'SKETCH';
      }
      if (isClassicLayer(layer)) {
        return 'CLASSIC';
      }
      const externalLayerServiceTypeKey = getSetting<string>(
        layer,
        LayerSettingsKey.toPersistenceKey('EXTERNAL_LAYER_SERVICE_TYPE'),
      );
      if (externalLayerServiceTypeKey) {
        const serviceType = ExternalLayerServiceType.fromPersistenceKey(externalLayerServiceTypeKey);
        switch (serviceType) {
          case 'WMS':
            return 'EXTERNAL_WMS';
          case 'WMTS':
            return 'EXTERNAL_WMTS';
          case 'VECTOR_TILE':
            return 'EXTERNAL_VECTOR_TILE';
          case 'ARCGIS_MAP':
            return 'EXTERNAL_ARCGIS_MAP';
          case 'ARCGIS_IMAGE':
            return 'EXTERNAL_ARCGIS_IMAGE';
          default:
            assertUnreachable(serviceType);
        }
      }
      throw new Error('Unknown layer type');
    }
  }
}

export function getSingleWorkbookLayerType(layer: WorkbookLayerDto) {
  const workbookLayerType = getWorkbookLayerType(layer);
  assertSingleWorkbookLayerType(workbookLayerType);
  return workbookLayerType;
}

export function toCatalogLayerType(workbookLayerType: WorkbookLayerType): CatalogLayerType | undefined {
  switch (workbookLayerType) {
    case 'STANDARD_GROUP':
    case 'TOGGLE_GROUP':
      return 'group';
    case 'DATA_VIEW':
      return 'data-view';
    case 'SKETCH':
      return 'sketch';
    case 'EXTERNAL_WMS':
    case 'EXTERNAL_WMTS':
    case 'EXTERNAL_VECTOR_TILE':
    case 'EXTERNAL_ARCGIS_MAP':
    case 'EXTERNAL_ARCGIS_IMAGE':
      return 'external';
    case 'CLASSIC':
      return undefined;
    default:
      assertUnreachable(workbookLayerType);
  }
}
export function isSingleLayer(layer: WorkbookLayerDto) {
  return layer.structureType === 'SINGLE';
}

export function isGroupLayer(layer: WorkbookLayerDto) {
  return !isSingleLayer(layer);
}

export function isDataViewLayer(layer: WorkbookLayerDto) {
  return isSingleLayer(layer) && !!layer.settings['dataViewId'] && !isSketchLayer(layer);
}

export function isSketchLayer(layer: WorkbookLayerDto) {
  return Boolean(getSetting(layer.settings, LayerSettingsKey.toPersistenceKey('SKETCH_LAYER_CONTENT_IDENTIFIER')));
}

export function isWritableSketchLayer(layer: WorkbookLayerDto) {
  return isSketchLayer(layer) && isWorkbookWritable(getStore().getState());
}

export function isExternalLayer(layer: WorkbookLayerDto) {
  return Boolean(getSetting(layer.settings, LayerSettingsKey.toPersistenceKey('EXTERNAL_LAYER_SERVICE_TYPE')));
}

export function isClassicLayer(layer: WorkbookLayerDto) {
  return Boolean(getSetting(layer.settings, LayerSettingsKey.toPersistenceKey('IS_CLASSIC_LAYER')));
}
